import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { DataSourceActivity, DataRequest } from "../types/api";
import { ImportWizard, UploadParams } from "./ImportWizard";

// TODO (backend): Add "Needs Response" tab for requests with unresolved
// uploader comments/questions. Fulfilled requests with new comments could
// re-appear here for follow-up.

type Tab = "forwarded" | "accepted" | "activity";

interface UploadingRequest {
  requestId: number;
}

const DataSourcePortal: React.FC = () => {
  const { t } = useTranslation(["dataSourcePortal", "common"]);
  const [tab, setTab] = useState<Tab>("forwarded");
  const [forwarded, setForwarded] = useState<DataRequest[]>([]);
  const [accepted, setAccepted] = useState<DataRequest[]>([]);
  const [activity, setActivity] = useState<DataSourceActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responding, setResponding] = useState<number | null>(null);
  const [uploadingRequest, setUploadingRequest] =
    useState<UploadingRequest | null>(null);
  const [user, setUser] = useState<{ id: number } | null>(null);

  const loadForwarded = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getDataSourceRedirectedRequests();
      setForwarded(res.requests ?? []);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("errors.loadForwardedFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAccepted = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getDataSourceAcceptedRequests();
      setAccepted(res.requests ?? []);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("errors.loadAcceptedFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getDataSourceActivity();
      setActivity(res.activity ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.loadActivityFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [fwd, acc, profile] = await Promise.all([
          apiService.getDataSourceRedirectedRequests(),
          apiService.getDataSourceAcceptedRequests(),
          apiService.getProfile(),
        ]);
        setForwarded(fwd.requests ?? []);
        setAccepted(acc.requests ?? []);
        setUser({ id: profile.user.id });
      } catch (e) {
        setError(e instanceof Error ? e.message : t("errors.loadDataFailed"));
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    if (tab === "forwarded") loadForwarded();
    else if (tab === "accepted") loadAccepted();
    else loadActivity();
  }, [tab]);

  const handleAccept = async (requestId: number) => {
    setResponding(requestId);
    try {
      await apiService.acceptRedirectedRequest(requestId);
      setForwarded((prev) => prev.filter((r) => r.id !== requestId));
      await loadAccepted();
      // Automatically open upload form for this request
      setTab("accepted");
      setUploadingRequest({ requestId });
    } catch (e) {
      alert(e instanceof Error ? e.message : t("errors.acceptFailed"));
    } finally {
      setResponding(null);
    }
  };

  const handleDecline = async (requestId: number) => {
    setResponding(requestId);
    try {
      await apiService.declineRedirectedRequest(requestId);
      setForwarded((prev) => prev.filter((r) => r.id !== requestId));
    } catch (e) {
      alert(e instanceof Error ? e.message : t("errors.declineFailed"));
    } finally {
      setResponding(null);
    }
  };

  const handleUpload = async (requestId: number, params: UploadParams) => {
    await apiService.uploadDataForRequest(
      requestId,
      params.file,
      params.displayName || undefined,
      undefined,
      params.metadataScope === "projects",
      params.piiFindingsAcknowledged,
      params.piiValueRedactions,
      params.piiDroppedColumns,
      params.parseOptions,
    );
    // Remove from accepted list after successful upload
    setAccepted((prev) => prev.filter((r) => r.id !== requestId));
    setUploadingRequest(null);
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const tabLabels: Record<Tab, string> = {
    forwarded: forwarded.length
      ? t("tabs.newRequestsWithCount", { n: forwarded.length })
      : t("tabs.newRequests"),
    accepted: accepted.length
      ? t("tabs.acceptedWithCount", { n: accepted.length })
      : t("tabs.accepted"),
    activity: t("tabs.activityLog"),
  };

  const FieldBadge = ({ label }: { label: string }) => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        background: "#e8e0f0",
        color: "#5b21b6",
        borderRadius: "12px",
        fontSize: "12px",
        marginRight: "4px",
        marginBottom: "2px",
      }}
    >
      {label}
    </span>
  );

  const RequestMeta = ({
    req,
    showRedirector = false,
  }: {
    req: DataRequest;
    showRedirector?: boolean;
  }) => (
    <div
      style={{
        color: "#64748b",
        fontSize: "13px",
        display: "flex",
        flexWrap: "wrap",
        gap: "16px",
        marginBottom: "10px",
      }}
    >
      {req.user_name && (
        <span>
          <strong>{t("meta.researcher")}</strong> {req.user_name}
          {req.user_email ? ` (${req.user_email})` : ""}
        </span>
      )}
      {showRedirector && req.redirected_by_name && (
        <span>
          <strong>{t("meta.forwardedBy")}</strong> {req.redirected_by_name}
        </span>
      )}
      {req.project_title && (
        <span>
          <strong>{t("meta.project")}</strong> {req.project_title}
        </span>
      )}
      {req.dataset_id && (
        <span>
          <strong>{t("meta.datasetId")}</strong> #{req.dataset_id}
        </span>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: "960px", margin: "32px auto", padding: "0 16px" }}>
      <h1 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>
        {t("header.title")}
      </h1>
      <p style={{ color: "#666", marginBottom: "24px" }}>
        {t("header.subtitle")}
      </p>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "0",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "24px",
        }}
      >
        {(["forwarded", "accepted", "activity"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "10px 20px",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontWeight: tab === t ? 700 : 400,
              borderBottom:
                tab === t ? "2px solid #7c3aed" : "2px solid transparent",
              color: tab === t ? "#7c3aed" : "#64748b",
              marginBottom: "-2px",
              fontSize: "14px",
              whiteSpace: "nowrap",
            }}
          >
            {tabLabels[t]}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          {t("common:status.loading")}
        </div>
      )}
      {error && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fee2e2",
            color: "#dc2626",
            borderRadius: "6px",
            marginBottom: "16px",
          }}
        >
          {error}
        </div>
      )}

      {/* ── Forwarded Requests ── */}
      {!loading && tab === "forwarded" && (
        <>
          {forwarded.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📨</div>
              <p>{t("forwardedTab.empty")}</p>
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {forwarded.map((req) => (
                <div
                  key={req.id}
                  style={{
                    border: "1px solid #fcd34d",
                    borderRadius: "8px",
                    padding: "20px",
                    background: "#fffbeb",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "6px",
                        }}
                      >
                        <span style={{ fontWeight: 700, fontSize: "16px" }}>
                          {req.title}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            background: "#fef3c7",
                            color: "#92400e",
                            borderRadius: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {t("badges.requestNumber", { id: req.id })}
                        </span>
                      </div>

                      <RequestMeta req={req} />

                      <div
                        style={{
                          color: "#374151",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        {req.description}
                      </div>

                      {req.requested_fields &&
                        req.requested_fields.length > 0 && (
                          <div style={{ marginBottom: "8px" }}>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontWeight: 600,
                                marginRight: "6px",
                              }}
                            >
                              {t("labels.requestedFields")}
                            </span>
                            {req.requested_fields.map((f, i) => (
                              <FieldBadge key={i} label={f} />
                            ))}
                          </div>
                        )}

                      {req.filter_criteria &&
                        req.filter_criteria.length > 0 && (
                          <div style={{ marginBottom: "8px" }}>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontWeight: 600,
                                marginRight: "6px",
                              }}
                            >
                              {t("labels.filterCriteria")}
                            </span>
                            {req.filter_criteria.map((f, i) => (
                              <span
                                key={i}
                                style={{
                                  fontSize: "13px",
                                  color: "#374151",
                                  marginRight: "8px",
                                }}
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        )}

                      {req.processing_notes && (
                        <div
                          style={{
                            color: "#64748b",
                            fontSize: "13px",
                            marginBottom: "8px",
                          }}
                        >
                          <strong>{t("labels.processingNotes")}</strong>{" "}
                          {req.processing_notes}
                        </div>
                      )}

                      {req.redirect_notes && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px 12px",
                            background: "#fef3c7",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#92400e",
                          }}
                        >
                          <strong>{t("labels.forwardingNote")}</strong>{" "}
                          {req.redirect_notes}
                        </div>
                      )}

                      {req.redirected_at && (
                        <div
                          style={{
                            color: "#94a3b8",
                            fontSize: "12px",
                            marginTop: "8px",
                          }}
                        >
                          {t("labels.forwardedOn", {
                            date: formatDate(req.redirected_at),
                          })}
                          {req.redirected_by_name &&
                            t("labels.forwardedBySuffix", {
                              name: req.redirected_by_name,
                            })}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: "160px",
                      }}
                    >
                      <button
                        onClick={() => handleAccept(req.id)}
                        disabled={responding === req.id}
                        style={{
                          padding: "10px 20px",
                          background: "#22c55e",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          cursor:
                            responding === req.id ? "not-allowed" : "pointer",
                          fontWeight: 600,
                          fontSize: "14px",
                          opacity: responding === req.id ? 0.6 : 1,
                        }}
                      >
                        {responding === req.id ? (
                          t("actions.processing")
                        ) : (
                          <>✓ {t("actions.canProvide")}</>
                        )}
                      </button>
                      <button
                        onClick={() => handleDecline(req.id)}
                        disabled={responding === req.id}
                        style={{
                          padding: "10px 20px",
                          background: "#fff",
                          color: "#dc2626",
                          border: "1px solid #dc2626",
                          borderRadius: "6px",
                          cursor:
                            responding === req.id ? "not-allowed" : "pointer",
                          fontWeight: 600,
                          fontSize: "14px",
                          opacity: responding === req.id ? 0.6 : 1,
                        }}
                      >
                        ✗ {t("actions.cannotProvide")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Accepted – Awaiting Upload ── */}
      {!loading && tab === "accepted" && (
        <>
          {accepted.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📦</div>
              <p>{t("acceptedTab.empty")}</p>
              <p style={{ fontSize: "13px", marginTop: "8px" }}>
                {t("acceptedTab.emptyHint")}
              </p>
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {accepted.map((req) => (
                <div
                  key={req.id}
                  style={{
                    border: "1px solid #7c3aed",
                    borderRadius: "8px",
                    padding: "20px",
                    background: "#faf5ff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: "16px" }}>
                      {req.title}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        background: "#ede9fe",
                        color: "#6d28d9",
                        borderRadius: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {t("badges.requestNumber", { id: req.id })}
                    </span>
                  </div>

                  <RequestMeta req={req} showRedirector={true} />

                  <div
                    style={{
                      color: "#374151",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    {req.description}
                  </div>

                  {req.requested_fields && req.requested_fields.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: 600,
                          marginRight: "6px",
                        }}
                      >
                        {t("labels.requestedFields")}
                      </span>
                      {req.requested_fields.map((f, i) => (
                        <FieldBadge key={i} label={f} />
                      ))}
                    </div>
                  )}

                  {/* Upload panel - reuse the ImportWizard component */}
                  {uploadingRequest?.requestId === req.id && user ? (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "16px",
                        background: "#fff",
                        border: "1px dashed #c4b5fd",
                        borderRadius: "8px",
                      }}
                    >
                      <ImportWizard
                        key={`upload-${req.id}`}
                        projectId={req.project_id}
                        currentUserId={user.id}
                        defaultDownloadScope="project"
                        uploadType="data_source"
                        inspectRoute={`/data-source/accepted/${req.id}/upload/inspect`}
                        onUpload={(params) => handleUpload(req.id, params)}
                        onCancel={() => setUploadingRequest(null)}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => setUploadingRequest({ requestId: req.id })}
                      style={{
                        marginTop: "4px",
                        padding: "10px 20px",
                        background: "#7c3aed",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      ↑ {t("actions.uploadDataFile")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Activity Log ── */}
      {!loading && tab === "activity" && (
        <>
          {activity.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}
            >
              {t("activityTab.empty")}
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                  {[
                    t("activityTab.headers.request"),
                    t("activityTab.headers.researcher"),
                    t("common:fields.project"),
                    t("activityTab.headers.dataset"),
                    t("common:fields.status"),
                    t("common:fields.date"),
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 12px",
                        textAlign: "left",
                        color: "#64748b",
                        fontWeight: 600,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activity.map((a) => (
                  <tr key={a.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px" }}>{a.request_title}</td>
                    <td style={{ padding: "12px" }}>
                      <div>{a.researcher_name}</div>
                      <div style={{ color: "#94a3b8", fontSize: "12px" }}>
                        {a.researcher_email}
                      </div>
                    </td>
                    <td style={{ padding: "12px" }}>{a.project_title}</td>
                    <td style={{ padding: "12px", color: "#6b7280" }}>
                      #{a.dataset_id}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 600,
                          background: a.confirmed_at ? "#d1fae5" : "#fef3c7",
                          color: a.confirmed_at ? "#065f46" : "#92400e",
                        }}
                      >
                        {a.confirmed_at
                          ? t("activityTab.confirmed")
                          : t("common:status.pending")}
                      </span>
                    </td>
                    <td style={{ padding: "12px", color: "#64748b" }}>
                      {a.confirmed_at
                        ? formatDate(a.confirmed_at)
                        : formatDate(a.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default DataSourcePortal;
